import { Box, Button, CircularProgress } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { Text } from "@src/appV2/redesign/components/Text";
import { berlinRebrandTheme } from "@src/appV2/redesign/theme/theme";

import clipboardWordmark from "../../../public/assets/clipboard-wordmark-brown.png";

interface MobileAuthLoadingProps {
  error?: string;
  onRetry?: () => void;
}

/**
 * Full-screen loading/error screen shown inside the native Capacitor app
 * while Cognito authentication is in progress after the user taps "Open"
 * from the email link.
 */
export function MobileAuthLoading({ error, onRetry }: MobileAuthLoadingProps) {
  return (
    <ThemeProvider theme={berlinRebrandTheme}>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.baseDark",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            paddingX: 5,
            paddingTop: "max(24px, env(safe-area-inset-top))",
            paddingBottom: "max(24px, env(safe-area-inset-bottom))",
            justifyContent: "center",
            alignItems: "center",
            gap: 7,
          }}
        >
          {/* Wordmark */}
          <img src={clipboardWordmark} alt="Clipboard" style={{ height: 70, width: "auto" }} />

          {!error && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 7,
              }}
            >
              <CircularProgress size={48} sx={{ color: "primary.main" }} />
              <Text bold variant="body1" sx={{ color: "base.base" }}>
                Signing you in...
              </Text>
            </Box>
          )}

          {error && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Text bold variant="h5" sx={{ color: "error.main", textAlign: "center" }}>
                Sign in failed
              </Text>
              <Text bold variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
                {error}
              </Text>
              {onRetry && (
                <Button
                  variant="text"
                  sx={{
                    textTransform: "none",
                    fontSize: "0.875rem",
                    color: "primary.main",
                  }}
                  onClick={onRetry}
                >
                  Back to login
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
